import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setAuthToken } from "./api";
import { authenticateWithPasskey } from "./passkey";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [suspendedInfo, setSuspendedInfo] = useState(null); // {reason, suspended_at, message}

    const checkAuth = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            setSuspendedInfo(null);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (
                err?.response?.status === 403 &&
                detail &&
                typeof detail === "object" &&
                detail.code === "account_suspended"
            ) {
                setSuspendedInfo(detail);
            }
            // 401 → token is stale/missing → drop it so we don't keep sending
            // a dead Bearer header on every request.
            if (err?.response?.status === 401) {
                setAuthToken("");
            }
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        checkAuth();
    }, [checkAuth]);

    const login = async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            // Persist JWT so the Bearer header is sent on every request — vital
            // for Safari where cross-site cookies are blocked.
            if (data.token) setAuthToken(data.token);
            setUser(data.user);
            setSuspendedInfo(null);
            return data.user;
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (
                err?.response?.status === 403 &&
                detail &&
                typeof detail === "object" &&
                detail.code === "account_suspended"
            ) {
                setSuspendedInfo(detail);
            }
            throw err;
        }
    };

    const loginWithPasskey = async (email = "") => {
        try {
            const data = await authenticateWithPasskey(email);
            if (data.token) setAuthToken(data.token);
            setUser(data.user);
            setSuspendedInfo(null);
            return data.user;
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (
                err?.response?.status === 403 &&
                detail &&
                typeof detail === "object" &&
                detail.code === "account_suspended"
            ) {
                setSuspendedInfo(detail);
            }
            throw err;
        }
    };

    const register = async (email, password, name, termsAccepted = false) => {
        const { data } = await api.post("/auth/register", {
            email, password, name, terms_accepted: termsAccepted,
        });
        if (data.token) setAuthToken(data.token);
        setUser(data.user);
        return data.user;
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch {
            // ignore
        }
        setAuthToken("");
        setUser(null);
        setSuspendedInfo(null);
    };

    const updateUser = (partial) => setUser((u) => ({ ...u, ...partial }));

    return (
        <AuthContext.Provider
            value={{
                user,
                loading,
                login,
                loginWithPasskey,
                register,
                logout,
                updateUser,
                refresh: checkAuth,
                suspendedInfo,
                clearSuspended: () => setSuspendedInfo(null),
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
};
